export type DetectedAspectRatio = '16:9' | '9:16';

export type SizeLike = {
  width?: number | null;
  height?: number | null;
};

export function orientationFromSize(
  width: number,
  height: number,
): 'landscape' | 'portrait' {
  return width >= height ? 'landscape' : 'portrait';
}

export function aspectRatioFromOrientation(
  orientation: 'landscape' | 'portrait',
): DetectedAspectRatio {
  return orientation === 'landscape' ? '16:9' : '9:16';
}

export type OrientationCounts = {
  landscape: number;
  portrait: number;
};

export function countOrientations(sizes: SizeLike[]): OrientationCounts {
  let landscape = 0;
  let portrait = 0;

  for (const size of sizes) {
    const width = Number(size.width);
    const height = Number(size.height);
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
      continue;
    }
    if (width >= height) landscape += 1;
    else portrait += 1;
  }

  return { landscape, portrait };
}

/** Majority of valid sizes. Landscape wins a tie. Returns null when nothing is measurable. */
export function detectAspectRatioFromSizes(sizes: SizeLike[]): DetectedAspectRatio | null {
  const { landscape, portrait } = countOrientations(sizes);
  if (landscape === 0 && portrait === 0) return null;
  if (portrait > landscape) return '9:16';
  return '16:9';
}

export function hasMixedOrientations(sizes: SizeLike[]): boolean {
  const { landscape, portrait } = countOrientations(sizes);
  return landscape > 0 && portrait > 0;
}

export function readImageSizeFromFile(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const src = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(src);
      resolve({
        width: img.naturalWidth || img.width,
        height: img.naturalHeight || img.height,
      });
    };
    img.onerror = () => {
      URL.revokeObjectURL(src);
      reject(new Error('Failed to read image size'));
    };
    img.src = src;
  });
}

export async function detectAspectRatioFromImageFiles(
  files: File[],
): Promise<DetectedAspectRatio> {
  const sizes = await Promise.all(
    files.map(async (file) => {
      try {
        return await readImageSizeFromFile(file);
      } catch {
        return { width: 0, height: 0 };
      }
    }),
  );
  return detectAspectRatioFromSizes(sizes) ?? '16:9';
}
